import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Users, Home as HomeIcon } from "lucide-react";

export default function ProgramPage({
  kind = "womens",
  title,
  subtitle,
  accent,
  motto,
  heroImage,
  galleryImages = [],
  highlights,
  welcomeList,
  testId,
}) {
  return (
    <div data-testid={testId}>
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a2e] via-[#240a3d] to-[#2e0e54]" />
          <div className="absolute -top-40 right-0 w-[520px] h-[520px] glow-gold rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 pb-20 lg:pt-28 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
                {kind === "womens" ? "Women's Haven" : "Men's Haven"}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-[#f7f2e7]">
              {title}
              <br />
              <span className="gold-text">{accent}</span>
            </h1>
            <p className="font-serif italic text-2xl text-[#b395d6] mt-4">{motto}</p>
            <p className="mt-6 text-lg text-[#dcd0b3] max-w-xl leading-relaxed">
              {subtitle}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/apply" data-testid="program-apply-btn" className="btn-gold">
                Apply Now <ArrowRight size={18} />
              </Link>
              <Link to="/contact" data-testid="program-contact-btn" className="btn-outline-gold">
                Ask a Question
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#d4af37]/30 to-transparent blur-2xl" />
            <div className="relative rounded-[24px] overflow-hidden gold-border">
              <img src={heroImage} alt={title} className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e] via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="section-pad bg-[#15082a]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">What you&apos;ll find</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
              A home built around <span className="gold-text">your next chapter</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {highlights.map((h) => (
              <div key={h.title} className="card-purple p-7">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8902c] flex items-center justify-center text-[#1a0a2e]">
                  <h.Icon size={20} />
                </div>
                <div className="font-serif text-2xl text-[#f7f2e7] mt-4">{h.title}</div>
                <div className="text-[#cdbfa1] mt-2 text-sm leading-relaxed">{h.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WELCOME */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="card-purple p-10">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">Who We Welcome</div>
            <h3 className="font-serif text-3xl md:text-4xl text-[#f7f2e7] mt-3">
              Real people. <span className="gold-text">Real progress.</span>
            </h3>
            <ul className="mt-7 space-y-3.5">
              {welcomeList.map((w) => (
                <li key={w} className="flex items-start gap-3 text-[#e8dcc0]">
                  <CheckCircle2 size={18} className="text-[#d4af37] shrink-0 mt-0.5" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {galleryImages.map((g, i) => (
              <img
                key={g}
                src={g}
                alt=""
                className={`rounded-2xl gold-border object-cover w-full ${
                  i === 0 ? "h-72 col-span-2" : "h-44"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-gradient-to-br from-[#240a3d] to-[#3d1465]">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-[#f7f2e7]">
            Ready to call this <span className="gold-text">home</span>?
          </h2>
          <p className="text-[#dcd0b3] mt-5">
            Applications are reviewed personally. We&apos;d love to hear your story.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/apply" data-testid="program-cta-apply" className="btn-gold">
              Start Application <ArrowRight size={18} />
            </Link>
            <a href="tel:7064318996" className="btn-outline-gold">Call 706-431-8996</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export function WomensHaven() {
  return (
    <ProgramPage
      testId="womens-page"
      kind="womens"
      title="Women's Haven."
      accent="A Brighter Future."
      motto="She builds. She heals. She rises."
      subtitle="A peaceful, well-maintained home environment where women 30+ rebuild stability, find community, and step confidently into independence."
      heroImage="https://images.unsplash.com/photo-1616137422495-1e9e46e2aa77?auto=format&fit=crop&w=1400&q=80"
      galleryImages={[
        "https://customer-assets.emergentagent.com/job_site-builder-3580/artifacts/y65ic5kv_image.png",
        "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=900&q=80",
        "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=900&q=80",
      ]}
      highlights={[
        { title: "Safe & Secure", desc: "Comfortable, well-maintained homes set in peaceful, quiet neighborhoods.", Icon: ShieldCheck },
        { title: "Supportive Community", desc: "An uplifting environment that promotes respect, encouragement, and growth.", Icon: Users },
        { title: "Independent Living", desc: "We empower women to live independently and achieve their personal goals.", Icon: HomeIcon },
      ]}
      welcomeList={[
        "Women ages 30 and older",
        "Seeking stable, affordable housing",
        "Transitioning from life challenges",
        "Must have verified, guaranteed income",
        "Committed to personal growth & independent living",
      ]}
    />
  );
}

export function MensHaven() {
  return (
    <ProgramPage
      testId="mens-page"
      kind="mens"
      title="Men's Haven."
      accent="A Stronger Future."
      motto="Focus. Discipline. Growth. Success."
      subtitle="A respectful, structured living environment for men 30+ committed to accountability, progress, and building a stable life with purpose."
      heroImage="https://images.unsplash.com/photo-1617104678098-de229db51175?auto=format&fit=crop&w=1400&q=80"
      galleryImages={[
        "https://customer-assets.emergentagent.com/job_site-builder-3580/artifacts/y65ic5kv_image.png",
        "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&w=900&q=80",
        "https://images.unsplash.com/photo-1484101403633-562f891dc89a?auto=format&fit=crop&w=900&q=80",
      ]}
      highlights={[
        { title: "Safe & Secure", desc: "Well-maintained homes located in safe, quiet, respectful neighborhoods.", Icon: ShieldCheck },
        { title: "Supportive Community", desc: "A structured environment that encourages growth, success, and accountability.", Icon: Users },
        { title: "Independent Living", desc: "We empower men to live independently and build a future of purpose.", Icon: HomeIcon },
      ]}
      welcomeList={[
        "Men ages 30 and older",
        "Seeking stable, affordable housing",
        "Transitioning from life challenges",
        "Must have verified, guaranteed income",
        "Committed to building a better future",
      ]}
    />
  );
}
